import { ParsedAnswer } from '../../Teleexpertise/questionTypes/_props'
import {
    CURRENT_QUESTION_KEY,
    SKIPPED_QUESTIONS_KEY,
} from '../../Teleexpertise/plannedExpertiseWrorkflow/workflow/constants'

export type ParsedTransmissionRequest = {
    answers: ParsedAnswer[]
    answersByQuestionId: Map<string, any[]>
    skippedQuestionIds: string[]
    currentQuestionId: string | null
}

const parseItem = (rawItem: any) => {
    try {
        return typeof rawItem === 'string' ? JSON.parse(rawItem) : rawItem
    } catch {
        return null
    }
}

const extractValue = (item: any) => {
    if (item.value && typeof item.value === 'object' && !Array.isArray(item.value) && item.value.value !== undefined) {
        return item.value.value
    }
    return item.value
}

const isSketchStrokes = (value: unknown) => typeof value === 'string' && value.startsWith('[')

type TransmissionMedias = { pictures?: any[] | null; documents?: any[] | null; videos?: any[] | null }

const getMediaUrlsByQuestionId = (transmission?: TransmissionMedias | null): Map<string, string[]> => {
    const urlsByQuestionId = new Map<string, string[]>()
    const medias = [transmission?.pictures, transmission?.documents, transmission?.videos]

    medias.forEach((list) =>
        (list ?? []).forEach((rawMedia: any) => {
            const media = rawMedia?.value ?? rawMedia
            const stepId = media?.transmissionStepId
            if (typeof stepId !== 'string' || typeof media?.url !== 'string') return
            const questionId = stepId.replace('W.A.', 'W.Q.')
            urlsByQuestionId.set(questionId, [...(urlsByQuestionId.get(questionId) ?? []), media.url])
        }),
    )
    return urlsByQuestionId
}

/**
 * reads the request of a transmission, whichever app wrote it
 *
 * ids drafts: { id: 'W.Q.x', value } + the special position / skipped keys
 * caregiver drafts: the full chatbot steps (sections, patient infos, W.Q.x question
 * steps with only a message/trigger, W.A.x answer steps with a value, or type: 'skip')
 *
 * medias complete the answers that are missing
 * only workflow answers are kept, everything else is ignored so it can't be mistaken for an answer.
 */
export const parseTransmissionRequest = (
    request: any[] | null | undefined,
    transmission?: TransmissionMedias | null,
): ParsedTransmissionRequest => {
    const answersByQuestionId = new Map<string, any[]>()
    const skipped = new Set<string>()
    let currentQuestionId: string | null = null

    for (const rawItem of request ?? []) {
        const item = parseItem(rawItem)
        const rawId = item?.id ?? item?.key
        if (typeof rawId !== 'string') continue

        if (rawId === CURRENT_QUESTION_KEY) {
            currentQuestionId = item.value ?? null
            continue
        }
        if (rawId === SKIPPED_QUESTIONS_KEY) {
            if (Array.isArray(item.value)) item.value.forEach((id: unknown) => skipped.add(String(id)))
            continue
        }

        const isAnswer = rawId.startsWith('W.A.')
        const isQuestion = rawId.startsWith('W.Q.')
        if (!isAnswer && !isQuestion) continue

        const questionId = isAnswer ? rawId.replace('W.A.', 'W.Q.') : rawId

        // a caregiver question step carries a message, not a value
        if (isQuestion && item.value === undefined) continue

        if (item.type === 'skip') {
            skipped.add(questionId)
            continue
        }

        const value = extractValue(item)
        if (value === null || value === undefined || value === '') continue
        answersByQuestionId.set(questionId, Array.isArray(value) ? value : [value])
    }

    // attachments uploaded but not yet written in the request
    getMediaUrlsByQuestionId(transmission).forEach((urls, questionId) => {
        const existing = answersByQuestionId.get(questionId) ?? []
        // a sketch drawn on ids is stored as strokes, its uploaded picture is only a copy for caregiver
        if (existing.some(isSketchStrokes)) return
        const missingUrls = urls.filter((url) => !existing.includes(url))
        if (missingUrls.length === 0) return
        // an uploaded media proves the question was answered, even if its step was written empty in the request
        skipped.delete(questionId)
        answersByQuestionId.set(questionId, [...existing, ...missingUrls])
    })

    const answers: ParsedAnswer[] = Array.from(answersByQuestionId.entries()).map(([id, values]) => ({
        id,
        value: values.length === 1 ? values[0] : values,
        auto: false,
    }))

    return {
        answers,
        answersByQuestionId,
        skippedQuestionIds: Array.from(skipped),
        currentQuestionId,
    }
}
